import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'

import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment, { Moment } from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import { isInclusivelyBeforeDay, SingleDatePicker } from 'react-dates'
import { useSelector } from 'react-redux'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import SvgCalendar from 'src/components/Svgs/SvgCalendar'
import SvgChevronLeft from 'src/components/Svgs/SvgChevronLeft'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface SingleDateInputFieldProps {
  id?: string
  className?: string
  fieldLabel?: React.ReactNode
  errors?: Array<React.ReactElement | string>
  dateMoment?: Moment
  onDateMomentChange: (val: Moment | undefined) => void
  showUpdates?: boolean
  baseValue?: Moment
  tooltip?: React.ReactElement
  allowAllDates?: boolean
  allowPastAndCurrentDates?: boolean
  placeHolder?: string
  isStatic?: boolean
}

const SingleDateInputField: React.FC<SingleDateInputFieldProps> = (
  props,
): React.ReactNode => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)

  const [showErrors, setShowErrors] = useState(props.errors !== undefined)
  useEffect((): void => {
    setShowErrors(props.errors !== undefined)
  }, [props.errors])

  const onDateChange = (date: Moment | null): void => {
    setShowErrors(false)
    props.onDateMomentChange(date || undefined)
  }

  const [focusedInput, setFocusedInput] = useState<boolean | null>(null)
  const onFocusChange = (arg: { focused: boolean | null }): void => {
    setFocusedInput(arg.focused)
  }

  const isUpdated =
    !!props.showUpdates &&
    props.baseValue?.dayOfYear() !== props.dateMoment?.dayOfYear()

  return (
    <div
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      {props.fieldLabel !== undefined && (
        <FieldLabel
          css={css`
            display: flex;
            align-items: center;
          `}
          isUpdated={isUpdated}
          dontHideOverflow
        >
          {!isUpdated || (isUpdated && props.baseValue === undefined) ? (
            <>{props.fieldLabel}</>
          ) : (
            props.baseValue !== undefined &&
            translate('${fieldLabel} (was ${baseValue})', {
              fieldLabel: props.fieldLabel,
              baseValue: props.baseValue?.format('ll'),
            })
          )}
          {props.tooltip !== undefined && props.tooltip}
        </FieldLabel>
      )}
      {props.isStatic ? (
        <span
          css={css`
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )}
          `}
        >
          {props.dateMoment !== undefined
            ? props.dateMoment?.format('MMM D, YYYY')
            : '--'}
        </span>
      ) : (
        <div
          data-testid={props.id ?? 'calendarInputField'}
          css={css`
            width: 100%;

            .SingleDatePicker {
              width: 100%;
            }

            .SingleDatePickerInput {
              display: flex;
              width: 100%;
              border: 1px solid ${styleConstants.colors.gray_medium};
              background-color: ${styleConstants.colors.gray_lightest};
              border-radius: 8px;

              &:focus,
              &:focus-within,
              &:hover {
                background-color: ${styleConstants.colors.white};
                border: 1px solid ${themeConstants.colors.cta};
              }
            }

            ${isUpdated &&
            css`
              .SingleDatePickerInput {
                border: 1px solid ${themeConstants.colors.highlight};
              }
            `}

            ${props.errors !== undefined &&
            showErrors &&
            css`
              .SingleDatePickerInput {
                border: 1px solid ${styleConstants.colors.red};
              }
            `}

        .SingleDatePickerInput_calendarIcon {
              padding: 10px 0px 10px 10px;
              margin: 0px;
            }

            .DateInput_1 {
              background-color: inherit;
              flex-grow: 2;
              border-radius: 8px;
            }

            .DateInput_input {
              margin: 0px;
              height: 48px;
              caret-color: ${themeConstants.colors.cta};
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
              background-color: inherit;
              border: none;
              border-radius: 8px;
              padding-bottom: 14px;

              &::placeholder {
                opacity: 0.5;
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
              }
            }

            ${isUpdated &&
            css`
              .DateInput_input {
                ${getFontCss(
                  14,
                  themeConstants.colors.highlight,
                  styleConstants.fontFamilies.regular,
                )};
              }
            `}

            ${props.errors !== undefined &&
            showErrors &&
            css`
              .DateInput_input {
                ${getFontCss(
                  14,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.regular,
                )};
              }
            `}

        .DateInput_fang {
              display: none;
            }

            .SingleDatePickerInput_arrow {
              display: none;
            }

            .SingleDatePicker_picker {
              left: 0px !important;
              z-index: 2;
            }

            .DayPicker {
              border-radius: 8px;
              border: 1px solid ${themeConstants.colors.cta};
              overflow: hidden;
            }

            .CalendarMonth_caption {
              ${getFontCss(
                14,
                themeConstants.colors.cta,
                styleConstants.fontFamilies.medium,
              )};
              padding-bottom: 48px;
            }

            .CalendarDay {
              border: 1px solid ${styleConstants.colors.gray_medium};
              background-color: ${styleConstants.colors.gray_lightest};

              &:hover {
                background-color: ${themeConstants.colors.cta_dark};
                ${getFontCss(
                  14,
                  styleConstants.colors.white,
                  styleConstants.fontFamilies.regular,
                )};
              }
            }

            .CalendarDay__blocked_out_of_range {
              ${getFontCss(
                14,
                styleConstants.colors.gray_dark,
                styleConstants.fontFamilies.regular,
              )};
              &:hover {
                background-color: ${styleConstants.colors.gray_lightest};
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_dark,
                  styleConstants.fontFamilies.regular,
                )};
              }
            }

            .CalendarDay__selected {
              background-color: ${themeConstants.colors.cta_dark};
            }

            .CalendarDay__hovered_span,
            .CalendarDay__selected_span {
              background-color: ${themeConstants.colors.cta_dark};
              opacity: 0.5;
              color: ${styleConstants.colors.white};
            }

            .DayPickerKeyboardShortcuts_buttonReset {
              display: none;
            }

            .DayPickerNavigation {
              position: absolute;
              top: 16px;
              padding: 0px 24px;
              display: flex;
              width: 100%;
              justify-content: space-between;
            }
          `}
        >
          <SingleDatePicker
            id={props.id ?? 'calendarInputField'}
            isOutsideRange={
              props.allowAllDates
                ? (): boolean => false
                : props.allowPastAndCurrentDates
                  ? (day: moment.Moment): boolean => {
                      return !isInclusivelyBeforeDay(day, moment())
                    }
                  : undefined
            }
            placeholder={
              props.placeHolder ||
              translateSingleString(locale, 'Select a date')
            }
            date={props.dateMoment || null}
            onDateChange={onDateChange}
            displayFormat={'ll'}
            focused={!!focusedInput}
            onFocusChange={onFocusChange}
            initialVisibleMonth={(): Moment => props.dateMoment || moment()}
            numberOfMonths={1}
            navPrev={
              <SvgChevronLeft
                data-testid={'DateInputFieldPreviousBtn'}
                css={css`
                  transform: scale(1.2);
                `}
              />
            }
            navNext={
              <SvgChevronRight
                data-testid={'DateInputFieldNextBtn'}
                css={css`
                  transform: scale(1.2);
                `}
              />
            }
            customInputIcon={<SvgCalendar />}
          />
        </div>
      )}
      {showErrors && props.errors !== undefined && (
        <ErrorList errors={props.errors} />
      )}
    </div>
  )
}

export default React.memo(SingleDateInputField)
